import React from "react";

import FieldsContext from "../components/FieldsContext";
import { parsePath, getValueByPath, setValueByPath } from "../utils/utils";

function useField({ path, validate }) {
  const context = React.useContext(FieldsContext) || {};

  if (!context) {
    throw new Error(`useField must be used within a FieldsProvider`);
  }

  const { fields, onChange } = context;

  const parsedPath = React.useMemo(() => {
    return parsePath(path);
  }, [path]);

  const setFieldProp = React.useCallback(
    (prop, value) => {
      if (!onChange) {
        return;
      }

      onChange((prevFields) => {
        const prevValue = getValueByPath(prevFields?.[prop], parsedPath);
        if (prevValue === value) {
          return prevFields;
        }
        return {
          ...prevFields,
          [prop]: setValueByPath(prevFields?.[prop], parsedPath, value),
        };
      });
    },
    [onChange, parsedPath]
  );

  const setFieldValue = React.useCallback(
    (value) => {
      setFieldProp("values", value);
    },
    [setFieldProp]
  );

  const setFieldTouched = React.useCallback(
    (value) => {
      setFieldProp("touched", value);
    },
    [setFieldProp]
  );

  const setFieldError = React.useCallback(
    (value) => {
      setFieldProp("errors", value);
    },
    [setFieldProp]
  );

  const value = getValueByPath(fields?.values, parsedPath);
  const touched = getValueByPath(fields?.touched, parsedPath);
  const error = getValueByPath(fields?.errors, parsedPath);

  React.useLayoutEffect(() => {
    if (!validate) {
      return;
    }
    const nextError = validate(value);
    setFieldProp("errors", nextError);
  }, [setFieldProp, value, validate]);

  return {
    value,
    touched,
    error,
    setFieldValue,
    setFieldTouched,
    setFieldError,
  };
}

export default useField;
